import { useForm, Controller } from "react-hook-form";
import { useState, useEffect, useContext } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  TextField, Button, Typography, Box, Grid, MenuItem,
  FormControl, InputLabel, Select, FormHelperText,
  Alert, Switch, FormControlLabel, Paper, Step,
  StepLabel, Stepper, Avatar, Snackbar, CircularProgress,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { getTipoDocumentos } from "../../../services/TipoDocumentoServices";
import { getProvincias } from "../../../services/ProvinciaServices";
import { getDepartamentos } from "../../../services/DepartamentosServices";
import { getLocalidades } from "../../../services/LocalidadesServices";
import { useNavigate, useLocation } from "react-router-dom";
import { ClienteFormContext } from "../../../context/cliente/ClienteContext";
import * as yup from "yup";
import { Email } from "@mui/icons-material";

// ─── Constantes ───────────────────────────────────────────────────────────────
const TIPO_IVA_OPTIONS = [
  { value: 1, label: "Responsable Inscripto" },
  { value: 5, label: "Monotributista" },
  { value: 4, label: "Consumidor Final" },
  { value: 2, label: "Exento" },
];

const STEPS = [
  { id: "datos", label: "Datos Personales" },
  { id: "direccion", label: "Dirección Legal" },
  { id: "entrega", label: "Dirección de Entrega" },
];

const CAMPOS_POR_PASO = {
  datos: ["tipoiva_id", "td_id", "cli_cuit", "cli_tel"],
  direccion: ["cli_direccion", "cli_nro_calle", "cli_cp", "pcia_id", "depto_id", "loc_id"],
  entrega: [],
};
const validation = yup.object({
  tipoiva_id: yup
    .number()
    .typeError("La condición de IVA es obligatoria")
    .required("La condición de IVA es obligatoria"),

  td_id: yup
    .number()
    .typeError("El tipo de documento es obligatorio")
    .required("El tipo de documento es obligatorio"),

  cli_cuit: yup
    .string()
    .required("El documento es obligatorio"),

  cli_tel: yup
    .string()
    .required("El teléfono es obligatorio"),

  cli_direccion: yup
    .string()
    .required("La dirección es obligatoria"),

  cli_nro_calle: yup
    .string()
    .required("El número de calle es obligatorio"),

  cli_cp: yup
    .string()
    .required("El código postal es obligatorio"),

  pcia_id: yup
    .number()
    .typeError("La provincia es obligatoria")
    .required("La provincia es obligatoria"),

  depto_id: yup
    .number()
    .typeError("El departamento es obligatorio")
    .required("El departamento es obligatorio"),

  loc_id: yup
    .number()
    .typeError("La localidad es obligatoria")
    .required("La localidad es obligatoria"),
});

const filtrarTiposDoc = (condicion, tipos) => {
  if (!condicion || tipos.length === 0) return [];
  let filtrados = [];
  if ([1, 5].includes(condicion)) {
    filtrados = tipos.filter(t => t.td_descripcion.toLowerCase() === "cuit");
  } else if (condicion === 4) {
    filtrados = tipos.filter(t => t.td_descripcion.toLowerCase() !== "cuit");
  } else {
    filtrados = [...tipos];
  }
  return filtrados.filter(t => t.id !== 242);
};

const menuProps = { MenuProps: { PaperProps: { style: { maxHeight: 48 * 5.5 } } } };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#f8fafc",
    transition: "all 0.18s ease",
    "& fieldset": { border: "1.5px solid #e2e8f0" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "2px" },
    "&.Mui-focused": { backgroundColor: "#fff" },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.875rem",
    "&.Mui-focused": { color: "#6366f1" },
  },
  "& .MuiFormHelperText-root": { fontSize: "0.72rem" },
};

// ─── Componente ───────────────────────────────────────────────────────────────
export const Form = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { updateAccount,getClientAccount } = useContext(ClienteFormContext);
  // Datos que vienen del login (mail + token de migración)
  const params = new URLSearchParams(location.search);

  const mail = params.get('email') || '';
  const token = params.get('token') || '';
  console.log("Datos recibidos en FormResetAccount:", { mail, token });
  const [activeStep, setActiveStep] = useState(0);
  const [tipoDocOptions, setTipoDocOptions] = useState([]);
  const [filteredTipoDocs, setFilteredTipoDocs] = useState([]);
  const [entregaDiferente, setEntregaDiferente] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingDatos, setLoadingDatos] = useState(true);
  const [successSnack, setSuccessSnack] = useState(false);

  const [provincias, setProvincias] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [localidades, setLocalidades] = useState([]);
  const [deptoEntrega, setDeptoEntrega] = useState([]);
  const [locEntrega, setLocEntrega] = useState([]);

  const {
    register, handleSubmit, control, watch, setValue, trigger,
    formState: { errors },
  } = useForm({ mode: "onChange", resolver: yupResolver(validation) });

  const condicionFiscal = watch("tipoiva_id");
  const provinciaId = watch("pcia_id");
  const departamentoId = watch("depto_id");
  const provinciaEntregaId = watch("cli_pcia_entrega");
  const deptoEntregaId = watch("cli_depto_entrega");


  // ── Carga inicial ──
  useEffect(() => {
    getTipoDocumentos().then(setTipoDocOptions).catch(console.error);
    getProvincias().then(r => setProvincias(r || [])).catch(console.error);
  }, []);

  // ── Prefill con datos del cliente desde el backend ──
  useEffect(() => {
    if (!mail || !token) return;
    const fetchDatos = async () => {
      try {
        const formData = { email: mail, token };
        const res = await getClientAccount(formData);
        console.log("Respuesta de getClientAccount:", res);
        const data = res.data;
        if (!data) return;

        // Seteamos todosss los campos que vengan con valor
        const campos = [
          'tipoiva_id', 'td_id', 'cli_cuit', 'razon_social', 'cli_tel',
          'cli_direccion', 'cli_nro_calle', 'cli_piso', 'cli_dpto', 'cli_cp',
          'pcia_id', 'depto_id', 'loc_id', 'cli_direccion_observ',
          'cli_direccion_entrega', 'cli_nro_calle_entrega', 'cli_piso_entrega',
          'cli_dpto_entrega', 'cli_cp_entrega', 'cli_pcia_entrega',
          'cli_depto_entrega', 'cli_loc_entrega', 'cli_direccion_entrega_observ',
        ];
        campos.forEach(k => { if (data[k]) setValue(k, data[k]); });

        if (data.cli_direccion_entrega || data.cli_pcia_entrega) {
          setEntregaDiferente(true);
        }
      } catch (e) {
        console.error('Error cargando datos del cliente:', e);
      } finally {
        setLoadingDatos(false);
      }
    };
    fetchDatos();
  }, [ ]);

  // ── Filtro tipo documento ──
  useEffect(() => {
    const filtrados = filtrarTiposDoc(condicionFiscal, tipoDocOptions);
    if (!filtrados.some(t => t.id === watch("td_id"))) setValue("td_id", "");
    setFilteredTipoDocs(filtrados);
  }, [condicionFiscal, tipoDocOptions]);

  useEffect(() => {
    if (![1, 5].includes(condicionFiscal)) {
      setValue("razon_social", "");
    }
  }, [condicionFiscal]);

  // ── Cascada geográfica legal ──
  useEffect(() => {
    if (!provinciaId) { setDepartamentos([]); return; }
    getDepartamentos(provinciaId).then(r => setDepartamentos(r || [])).catch(console.error);
    setValue("depto_id", "");
    setValue("loc_id", "");
  }, [provinciaId]);

  useEffect(() => {
    if (!departamentoId) { setLocalidades([]); return; }
    getLocalidades(departamentoId).then(r => setLocalidades(r || [])).catch(console.error);
    setValue("loc_id", "");
  }, [departamentoId]);

  // ── Cascada geográfica entrega ──
  useEffect(() => {
    if (!provinciaEntregaId) { setDeptoEntrega([]); return; }
    getDepartamentos(provinciaEntregaId).then(r => setDeptoEntrega(r || [])).catch(console.error);
    setValue("cli_depto_entrega", "");
    setValue("cli_loc_entrega", "");
  }, [provinciaEntregaId]);

  useEffect(() => {
    if (!deptoEntregaId) { setLocEntrega([]); return; }
    getLocalidades(deptoEntregaId).then(r => setLocEntrega(r || [])).catch(console.error);
    setValue("cli_loc_entrega", "");
  }, [deptoEntregaId]);

  // ── Navegación entre pasos ──
  const handleNext = async () => {
    const campos = CAMPOS_POR_PASO[STEPS[activeStep].id] || [];
    if (campos.length) {
      const ok = await trigger(campos);
      if (!ok) return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => setActiveStep(prev => prev - 1);

  // ── Submit ──
  const onSubmit = async (data) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const formData = { ...data,email: mail ,token };
      if (!entregaDiferente) {
        [
          "cli_direccion_entrega", "cli_nro_calle_entrega", "cli_piso_entrega",
          "cli_dpto_entrega", "cli_cp_entrega", "cli_pcia_entrega",
          "cli_depto_entrega", "cli_loc_entrega", "cli_direccion_entrega_observ",
        ].forEach(k => delete formData[k]);
      }
      await updateAccount(formData);
      setSuccessSnack(true);
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Ha ocurrido un error. Contactese con soporte.');
    } finally {
      setLoading(false);
    }
  };

  const pasoActual = STEPS[activeStep];
  const isLastStep = activeStep === STEPS.length - 1;

  // ─── Contenido por paso ───────────────────────────────────────────────────
  const renderContent = () => {
    if (loadingDatos) return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );

    switch (pasoActual.id) {

      case "datos":
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.tipoiva_id} sx={inputSx}>
                <InputLabel>Condición de IVA</InputLabel>
                <Controller name="tipoiva_id" control={control}
                  render={({ field }) => (
                    <Select label="Condición de IVA" value={field.value ?? ""}
                      onChange={e => field.onChange(Number(e.target.value))}>
                      {TIPO_IVA_OPTIONS.map(o => (
                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.tipoiva_id?.message}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!errors.td_id} sx={inputSx}>
                <InputLabel>Tipo de Documento</InputLabel>
                <Controller name="td_id" control={control}
                  render={({ field }) => (
                    <Select label="Tipo de Documento" value={field.value ?? ""}
                      onChange={e => field.onChange(Number(e.target.value))}>
                      {filteredTipoDocs.map(t => (
                        <MenuItem key={t.id} value={Number(t.id)}>{t.td_descripcion}</MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>{errors.td_id?.message}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller name="cli_cuit" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Número de Documento"
                    error={!!errors.cli_cuit} helperText={errors.cli_cuit?.message} sx={inputSx} />
                )}
              />
            </Grid>

            {[1, 5].includes(condicionFiscal) && (
              <Grid item xs={12} sm={6}>
                <TextField fullWidth size="small" label="Razón Social"
                  {...register("razon_social")}
                  error={!!errors.razon_social} helperText={errors.razon_social?.message}
                  sx={inputSx} />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <Controller name="cli_tel" control={control}
                render={({ field }) => (
                  <TextField {...field} fullWidth size="small" label="Teléfono"
                    error={!!errors.cli_tel} helperText={errors.cli_tel?.message} sx={inputSx} />
                )}
              />
            </Grid>
          </Grid>
        );

      case "direccion":
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Calle" {...register("cli_direccion")}
                error={!!errors.cli_direccion} helperText={errors.cli_direccion?.message} sx={inputSx} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField fullWidth size="small" label="Nro" {...register("cli_nro_calle")}
                error={!!errors.cli_nro_calle} helperText={errors.cli_nro_calle?.message} sx={inputSx} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField fullWidth size="small" label="Piso" {...register("cli_piso")} sx={inputSx} />
            </Grid>
            <Grid item xs={4} sm={2}>
              <TextField fullWidth size="small" label="Dpto" {...register("cli_dpto")} sx={inputSx} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="CP" {...register("cli_cp")}
                error={!!errors.cli_cp} helperText={errors.cli_cp?.message} sx={inputSx} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Provincia" {...register("pcia_id")}
                value={watch("pcia_id") || ""} SelectProps={menuProps}
                error={!!errors.pcia_id} helperText={errors.pcia_id?.message} sx={inputSx}>
                {provincias.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Departamento" {...register("depto_id")}
                value={watch("depto_id") || ""} SelectProps={menuProps}
                disabled={!departamentos.length}
                error={!!errors.depto_id} helperText={errors.depto_id?.message} sx={inputSx}>
                {departamentos.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select fullWidth size="small" label="Localidad" {...register("loc_id")}
                value={watch("loc_id") || ""} SelectProps={menuProps}
                disabled={!localidades.length}
                error={!!errors.loc_id} helperText={errors.loc_id?.message} sx={inputSx}>
                {localidades.map(l => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Observaciones de la dirección"
                {...register("cli_direccion_observ")} sx={inputSx} />
            </Grid>
          </Grid>
        );

      case "entrega":
        return (
          <>
            <FormControlLabel
              sx={{ mb: 2 }}
              control={
                <Switch checked={entregaDiferente}
                  onChange={e => setEntregaDiferente(e.target.checked)} color="primary" />
              }
              label={
                <Typography variant="body2" fontWeight={500}>
                  Tengo una dirección de entrega diferente a la legal
                </Typography>
              }
            />

            {!entregaDiferente && (
              <Alert severity="info" sx={{ borderRadius: "10px" }}>
                Se usará la dirección legal como dirección de entrega.
              </Alert>
            )}

            {entregaDiferente && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Calle" {...register("cli_direccion_entrega")}
                    error={!!errors.cli_direccion_entrega} helperText={errors.cli_direccion_entrega?.message} sx={inputSx} />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField fullWidth size="small" label="Nro" {...register("cli_nro_calle_entrega")} sx={inputSx} />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField fullWidth size="small" label="Piso" {...register("cli_piso_entrega")} sx={inputSx} />
                </Grid>
                <Grid item xs={4} sm={2}>
                  <TextField fullWidth size="small" label="Dpto" {...register("cli_dpto_entrega")} sx={inputSx} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField fullWidth size="small" label="Código Postal" {...register("cli_cp_entrega")} sx={inputSx} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth size="small" label="Provincia" {...register("cli_pcia_entrega")}
                    value={watch("cli_pcia_entrega") || ""} SelectProps={menuProps} sx={inputSx}>
                    {provincias.map(p => <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth size="small" label="Departamento" {...register("cli_depto_entrega")}
                    value={watch("cli_depto_entrega") || ""} SelectProps={menuProps}
                    disabled={!deptoEntrega.length} sx={inputSx}>
                    {deptoEntrega.map(d => <MenuItem key={d.id} value={d.id}>{d.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField select fullWidth size="small" label="Localidad" {...register("cli_loc_entrega")}
                    value={watch("cli_loc_entrega") || ""} SelectProps={menuProps}
                    disabled={!locEntrega.length} sx={inputSx}>
                    {locEntrega.map(l => <MenuItem key={l.id} value={l.id}>{l.label}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Observaciones de entrega"
                    {...register("cli_direccion_entrega_observ")} sx={inputSx} />
                </Grid>
              </Grid>
            )}
          </>
        );

      default: return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", alignItems: "flex-start", justifyContent: "center", py: { xs: 3, md: 6 }, px: 2 }}>
      <Box sx={{ width: "100%", maxWidth: 680 }}>

        {/* Header */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 3 }}>
          <Avatar sx={{ width: 52, height: 52, mb: 1.5, background: "linear-gradient(135deg, #6366f1, #8b5cf6)" }}>
            <AccountCircleIcon sx={{ fontSize: 28 }} />
          </Avatar>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: "1.3rem", sm: "1.5rem" }, color: "#1e293b", letterSpacing: "-0.02em" }}>
            Activá tu cuenta
          </Typography>
          <Typography sx={{ fontSize: "0.85rem", color: "#94a3b8", mt: 0.5, textAlign: 'center' }}>
            Revisá y completá tus datos para activar tu cuenta en el nuevo sistema
          </Typography>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{errorMessage}</Alert>
        )}

        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {STEPS.map((step, i) => (
            <Step key={step.id} completed={i < activeStep}>
              <StepLabel sx={{
                "& .MuiStepLabel-label": { fontSize: "0.78rem", fontWeight: activeStep === i ? 700 : 400 },
                "& .MuiStepIcon-root.Mui-active": { color: "#6366f1" },
                "& .MuiStepIcon-root.Mui-completed": { color: "#6366f1" },
              }}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Card del paso */}
        <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: "16px", border: "1.5px solid #e2e8f0", minHeight: 280 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#6366f1", mb: 2.5 }}>
            {pasoActual.label}
          </Typography>

          {renderContent()}

          {/* Navegación */}
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
            <Button
              variant="outlined" onClick={handleBack} disabled={activeStep === 0}
              startIcon={<ArrowBackIcon />}
              sx={{ borderRadius: "10px", textTransform: "none", borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" } }}
            >
              Anterior
            </Button>

            {isLastStep ? (
              <Button
                variant="contained" onClick={handleSubmit(onSubmit)}
                startIcon={loading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SaveIcon />}
                disabled={loading}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#6366f1", boxShadow: "0 4px 14px rgba(99,102,241,0.35)", "&:hover": { bgcolor: "#4f46e5" } }}
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            ) : (
              <Button
                variant="contained" onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
                sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: "#6366f1", boxShadow: "0 4px 14px rgba(99,102,241,0.35)", "&:hover": { bgcolor: "#4f46e5" } }}
              >
                Siguiente
              </Button>
            )}
          </Box>
        </Paper>

        {/* Indicador de paso */}
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 2 }}>
          {STEPS.map((_, i) => (
            <Box key={i} sx={{ width: i === activeStep ? 20 : 8, height: 8, borderRadius: "4px", bgcolor: i === activeStep ? "#6366f1" : "#e2e8f0", transition: "all 0.25s ease" }} />
          ))}
        </Box>
      </Box>

      {/* Snackbar de éxito */}
      <Snackbar
        open={successSnack}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          icon={<CheckCircleIcon />}
          severity="success"
          sx={{ borderRadius: "10px", fontWeight: 600, fontSize: "0.95rem" }}
        >
          ¡Tu cuenta fue actualizada exitosamente! Redirigiendo...
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Form;